import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { ApiResponse } from '../types/connection.types';
import type {
  IngestResumeMode,
  IngestResumeResponse,
  IngestRunLineage,
  IngestRunPages,
  IngestRunPagesParams,
} from '../types/ingest-run.types';

/**
 * Ingest run controls (onboarding Complete Import). The new pipeline-run routes
 * are camelCase on the wire, so — unlike the snake_case pipeline reads — nothing
 * is case-converted here (that would also mangle the UPPER_SNAKE status keys of
 * `countsByStatus`).
 */

/** Run chain the given run belongs to (works with any run in the chain). */
export async function getIngestRunLineage(runId: string): Promise<IngestRunLineage> {
  const res = await integrationsClient
    .get(endpoints.pipelines.runLineage(runId))
    .json<ApiResponse<IngestRunLineage>>();
  return res.data;
}

/** One server-side page of a run's per-page ledger. `ALL` is the default filter,
 *  so it is left off the query string. */
export async function getIngestRunPages(
  runId: string,
  params: IngestRunPagesParams,
): Promise<IngestRunPages> {
  const res = await integrationsClient
    .get(endpoints.pipelines.runPages(runId), {
      searchParams: {
        ...(params.status === 'ALL' ? {} : { status: params.status }),
        offset: params.offset,
        limit: params.limit,
      },
    })
    .json<ApiResponse<IngestRunPages>>();
  return res.data;
}

/** Start a new run from a FAILED/CANCELLED one (202). REMAINING keeps earlier
 *  pages; RESTART re-fetches from page 1. 409s carry a `code` (see types). */
export async function resumeIngestRun(
  runId: string,
  mode: IngestResumeMode,
): Promise<IngestResumeResponse> {
  const res = await integrationsClient
    .post(endpoints.pipelines.resumeRun(runId), { json: { mode } })
    .json<ApiResponse<IngestResumeResponse>>();
  return res.data;
}

/** The three in-place workflow controls the run card exposes. */
export type WorkflowControlAction = 'pause' | 'resume' | 'cancel';

/** POST /workflows/{id}/pause | resume | cancel. The body is ignored server-side. */
export async function controlWorkflow(
  workflowId: string,
  action: WorkflowControlAction,
): Promise<void> {
  await integrationsClient.post(endpoints.workflows[action](workflowId));
}
